import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/nocode';
import { sendEmail } from '../../api/ses';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import SafeIcon from '../../common/SafeIcon';

export default function Passes() {
  const [users, setUsers] = useState([]);
  const [passes, setPasses] = useState([]);
  const [settings, setSettings] = useState({});
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('Registered (Newest)');
  const [selectedIds, setSelectedIds] = useState([]);

  const [showCompPanel, setShowCompPanel] = useState(false);
  const [showWebhookPanel, setShowWebhookPanel] = useState(false);
  const [webhookMisses, setWebhookMisses] = useState([]);

  const [activeModal, setActiveModal] = useState(null);
  const [activeUser, setActiveUser] = useState(null);
  
  const [upgradeReason, setUpgradeReason] = useState('Skillplate Webhook Fix');
  const [upgradeNotes, setUpgradeNotes] = useState('');
  const [downgradeEmailConfirm, setDowngradeEmailConfirm] = useState('');
  const [extendDate, setExtendDate] = useState('');
  const [compForm, setCompForm] = useState({ email: '', name: '', reason: 'Scholarship', notes: '' });

  const loadData = async () => {
    setLoading(true);
    try {
      const [uRes, pRes, sRes, wRes] = await Promise.all([
        api.get('users_fbwm'),
        api.get('passes_fbwm'),
        api.get('settings_fbwm'),
        api.get('webhook_logs_fbwm')
      ]);
      
      setUsers((Array.isArray(uRes) ? uRes : uRes.data || []).filter(u => u.role !== 'admin'));
      setPasses(Array.isArray(pRes) ? pRes : pRes.data || []);
      
      const sets = Array.isArray(sRes) ? sRes : sRes.data || [];
      const setMap = {};
      sets.forEach(s => setMap[s.key] = s.value);
      setSettings(setMap);
      
      setWebhookLogs(Array.isArray(wRes) ? wRes : wRes.data || []);
    } catch (e) {
      console.error(e);
      showToast('Error loading data', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };
  
  const campers = users.map(u => {
    const userPasses = passes.filter(p => p.user_id === u.id).sort((a, b) => new Date(b.purchased_at) - new Date(a.purchased_at));
    const activePass = userPasses[0];
    
    let displayPassType = u.pass_type || 'free';
    let source = 'Unknown';
    let pricePaid = 0;
    let purchasedAt = null;
    let expiresAt = ['allaccess', 'evergreen-aap'].includes(u.pass_type) ? '2026-08-31T23:59:59Z' : '2026-07-19T23:59:59Z';

    if (activePass) {
      if (activePass.pass_type === 'comped') displayPassType = 'comped';
      source = activePass.source || 'Unknown';
      pricePaid = activePass.price_paid || 0;
      purchasedAt = activePass.purchased_at;
      if (activePass.expires_at) expiresAt = activePass.expires_at;
    }

    const isExpired = new Date(expiresAt) < new Date();

    return { ...u, displayPassType, source, pricePaid, purchasedAt, expiresAt, isExpired, history: userPasses };
  });

  const totalRegistered = campers.length;
  const freeCount = campers.filter(c => c.displayPassType === 'free').length;
  const evergreenCount = campers.filter(c => c.displayPassType === 'evergreen').length;
  const aaCount = campers.filter(c => ['allaccess', 'evergreen-aap'].includes(c.displayPassType)).length;
  const compedCount = campers.filter(c => c.displayPassType === 'comped').length;
  const totalRevenue = passes.reduce((acc, p) => acc + (parseFloat(p.price_paid) || 0), 0);
  const purchaseCount = passes.filter(p => parseFloat(p.price_paid) > 0).length;

  let filteredCamperList = campers.filter(c => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !c.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== 'All') {
        const filterKey = typeFilter.toLowerCase().replace('-', '');
        if (filterKey === 'allaccess' && !['allaccess', 'evergreen-aap'].includes(c.displayPassType)) return false;
        if (filterKey !== 'allaccess' && c.displayPassType.toLowerCase() !== filterKey) return false;
    }
    if (sourceFilter !== 'All' && c.source.toLowerCase() !== sourceFilter.toLowerCase()) return false;
    if (dateFrom && new Date(c.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(c.created_at) > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  filteredCamperList.sort((a, b) => {
    if (sortBy === 'Registered (Newest)') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === 'Name A-Z') return a.name.localeCompare(b.name);
    if (sortBy === 'Pass Type') return a.displayPassType.localeCompare(b.displayPassType);
    if (sortBy === 'Purchase Date') return new Date(b.purchasedAt || 0) - new Date(a.purchasedAt || 0);
    return 0;
  });

  const sendAccessEmail = async (user, isComped = false) => {
    const subject = isComped ? "You've been gifted an All-Access Pass!" : "Your Camp FaiBoWriMo All-Access Pass is Active";
    const body = `
      <div style="font-family: sans-serif; color: #1A2E0A; max-width: 600px; margin: 0 auto; background: #FDF6EC; padding: 40px; border-radius: 8px;">
        <h2 style="color: #2D5016;">Welcome to All-Access, ${user.name.split(' ')[0]}!</h2>
        <p>Your pass is officially active. You now have full access to the Compass Workbook, the Letter Studio, Bunkmate pairing, and lifetime replay access.</p>
        ${isComped ? '<p><i>You have been granted a comped pass by the director. Enjoy every moment of camp!</i></p>' : ''}
        <p>Log in to your dashboard to get started.</p>
      </div>
    `;
    await sendEmail(user.email, subject, body);
  };

  const handleExecuteUpgrade = async (isComped = false) => {
    if (!activeUser) return;
    try {
      let newPassType = 'allaccess';
      if (activeUser.pass_type === 'evergreen') newPassType = 'evergreen-aap';

      await api.put('users_fbwm', activeUser.id, { pass_type: newPassType });
      
      const passPayload = {
        id: crypto.randomUUID(),
        user_id: activeUser.id,
        pass_type: isComped ? 'comped' : newPassType,
        purchased_at: new Date().toISOString(),
        price_paid: isComped ? 0 : (settings.early_bird_deadline && new Date() < new Date(settings.early_bird_deadline) ? 29 : 49),
        expires_at: isComped ? '2099-12-31T23:59:59Z' : '2026-08-31T23:59:59Z',
        source: isComped ? 'comped' : (upgradeReason.includes('Skillplate') ? 'skillplate' : 'manual'),
        notes: upgradeNotes || upgradeReason,
        owner_id: 'admin'
      };
      await api.post('passes_fbwm', passPayload);

      await sendAccessEmail(activeUser, isComped);

      showToast(`Successfully upgraded ${activeUser.name}`);
      setActiveModal(null);
      loadData();
    } catch (e) {
      showToast('Failed to upgrade user', 'error');
    }
  };

  const handleDowngrade = async () => {
    if (downgradeEmailConfirm !== activeUser.email) {
      alert("Email does not match.");
      return;
    }
    try {
      let newPassType = 'free';
      if (activeUser.pass_type === 'evergreen-aap') newPassType = 'evergreen';

      await api.put('users_fbwm', activeUser.id, { pass_type: newPassType });
      
      if (activeUser.history && activeUser.history.length > 0) {
        await api.put('passes_fbwm', activeUser.history[0].id, { expires_at: new Date().toISOString() });
      }

      await sendEmail(activeUser.email, "Camp Pass Update", "<p>Your pass has been downgraded. You will lose access to All-Access features immediately.</p>");
      
      showToast(`Downgraded ${activeUser.name}`);
      setActiveModal(null);
      loadData();
    } catch (e) {
      showToast('Failed to downgrade user', 'error');
    }
  };

  const handleExtend = async () => {
    if (!extendDate || !activeUser.history[0]) return;
    try {
      const newExp = new Date(extendDate + 'T23:59:59Z').toISOString();
      await api.put('passes_fbwm', activeUser.history[0].id, { 
        expires_at: newExp, 
        notes: activeUser.history[0].notes + `\nExtended to ${extendDate} by Admin` 
      });
      showToast(`Extended access for ${activeUser.name}`);
      setActiveModal(null);
      loadData();
    } catch (e) {
      showToast('Failed to extend access', 'error');
    }
  };

  const handleQuickGrant = async (e) => {
    e.preventDefault();
    if (!compForm.email) return;
    try {
      let uId;
      const existing = users.find(u => u.email.toLowerCase() === compForm.email.toLowerCase());
      
      if (existing) {
        uId = existing.id;
        let newPassType = 'allaccess';
        if (existing.pass_type === 'evergreen') newPassType = 'evergreen-aap';
        await api.put('users_fbwm', uId, { pass_type: newPassType });
      } else {
        uId = crypto.randomUUID();
        await api.post('users_fbwm', {
          id: uId,
          name: compForm.name || 'Author',
          email: compForm.email,
          role: 'camper',
          pass_type: 'allaccess',
          owner_id: uId
        });
      }

      await api.post('passes_fbwm', {
        id: crypto.randomUUID(),
        user_id: uId,
        pass_type: 'comped',
        purchased_at: new Date().toISOString(),
        price_paid: 0,
        expires_at: '2099-12-31T23:59:59Z',
        source: 'comped',
        notes: `${compForm.reason} - ${compForm.notes}`,
        owner_id: 'admin'
      });

      await sendAccessEmail({ email: compForm.email, name: compForm.name || 'Author' }, true);
      
      showToast(`All-Access granted to ${compForm.email}`);
      setCompForm({ email: '', name: '', reason: 'Scholarship', notes: '' });
      setShowCompPanel(false);
      loadData();
    } catch (err) {
      showToast('Failed to grant comped pass', 'error');
    }
  };

  const checkWebhooks = () => {
    const misses = [];
    webhookLogs.forEach(log => {
      try {
        if (log.status === 'Success') {
          const payload = JSON.parse(log.payload);
          const email = payload?.user?.email;
          if (email) {
            const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
            if (user && ['free', 'evergreen'].includes(user.pass_type)) {
              misses.push({ user, log });
            }
          }
        }
      } catch (e) {
        console.error('Error parsing webhook payload', e);
      }
    });
    setWebhookMisses(misses);
  };

  const fixMissedWebhook = async (miss) => {
    setActiveUser(miss.user);
    setUpgradeReason('Skillplate Webhook Fix');
    setUpgradeNotes('Reconciled manually after webhook log review.');
    setActiveModal('upgrade');
  };

  const exportCSV = (dataToExport) => {
    const headers = ['Name', 'Email', 'Pass Type', 'Source', 'Purchase Date', 'Price Paid', 'Expires At', 'Genre', 'Journey Stage'];
    const rows = dataToExport.map(c => [
      `"${c.name}"`,
      `"${c.email}"`,
      c.displayPassType,
      c.source,
      c.purchasedAt ? new Date(c.purchasedAt).toLocaleDateString() : '—',
      c.pricePaid,
      c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—',
      c.genre || '',
      c.journey_stage || ''
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `camp_passes_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const sendBulkReminder = async () => {
    if (!window.confirm(`Send upgrade reminder to ${selectedIds.length} free/evergreen campers?`)) return;
    let count = 0;
    for (const id of selectedIds) {
      const u = campers.find(c => c.id === id);
      if (u && ['free', 'evergreen'].includes(u.displayPassType)) {
        await sendEmail(u.email, "Early Bird Ends Soon!", "<p>Don't forget to grab your All-Access pass before the price goes up!</p>");
        count++;
      }
    }
    showToast(`Sent ${count} reminders.`);
    setSelectedIds([]);
  };

  let priceWindow = 'Regular: $49';
  let priceColor = 'text-forest-green';
  let timeRemaining = '';
  if (settings.early_bird_deadline) {
    const ebDate = new Date(settings.early_bird_deadline);
    const now = new Date();
    if (now < ebDate) {
      priceWindow = 'Early Bird: $29';
      priceColor = 'text-amber';
      const diff = ebDate - now;
      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
      timeRemaining = `Ends in ${d}d ${h}h`;
    } else {
      timeRemaining = 'Early Bird Ended';
    }
  }

  const RowActions = ({ camper }) => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const toggle = (e) => {
      e.stopPropagation();
      if (!open && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + window.scrollY,
          left: rect.right + window.scrollX - 192,
        });
      }
      setOpen(!open);
    };

    useEffect(() => {
      if (!open) return;
      const close = () => setOpen(false);
      window.addEventListener('click', close);
      window.addEventListener('scroll', close, { passive: true, capture: true });
      window.addEventListener('resize', close);
      return () => {
        window.removeEventListener('click', close);
        window.removeEventListener('scroll', close, { capture: true });
        window.removeEventListener('resize', close);
      };
    }, [open]);

    return (
      <>
        <button ref={buttonRef} onClick={toggle} className="p-2 text-gray-400 hover:text-dark-green rounded-full hover:bg-gray-100 transition-colors">
          <SafeIcon name="MoreVertical" />
        </button>
        {open && createPortal(
          <div 
            className="absolute w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[9999] py-1"
            style={{ top: coords.top, left: coords.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => { setActiveUser(camper); setActiveModal('profile'); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="User" className="text-xs"/> View Profile</button>
            {['free', 'evergreen'].includes(camper.displayPassType) && (
              <>
                <button onClick={() => { setActiveUser(camper); setActiveModal('upgrade'); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-forest-green font-medium hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="ArrowUpCircle" className="text-xs"/> Upgrade to All-Access</button>
                <button onClick={() => { setActiveUser(camper); setActiveModal('comp'); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-teal-600 font-medium hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="Gift" className="text-xs"/> Grant Comped Pass</button>
              </>
            )}
            {!['free', 'evergreen'].includes(camper.displayPassType) && (
              <>
                <button onClick={() => { setActiveUser(camper); setActiveModal('extend'); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-amber hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="Calendar" className="text-xs"/> Extend Access</button>
                <button onClick={() => { setActiveUser(camper); setActiveModal('downgrade'); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-red-600 font-medium hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="ArrowDownCircle" className="text-xs"/> Downgrade</button>
              </>
            )}
            <button onClick={() => { setActiveUser(camper); setActiveModal('history'); setOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="Clock" className="text-xs"/> View Pass History</button>
            {!['free', 'evergreen'].includes(camper.displayPassType) && (
              <button onClick={() => { sendAccessEmail(camper, camper.displayPassType === 'comped'); setOpen(false); showToast('Email sent'); }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"><SafeIcon name="Mail" className="text-xs"/> Send Access Email</button>
            )}
          </div>,
          document.body
        )}
      </>
    );
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-20">
      {toast && (
        <div className={`fixed top-4 right-4 px-5 py-3 rounded-md shadow-lg z-50 animate-fadeIn text-white font-bold ${toast.type === 'error' ? 'bg-red-600' : 'bg-forest-green'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-poppins font-bold text-forest-green flex items-center gap-3">
            <SafeIcon name="Tag" /> Pass Management
          </h1>
          <p className="text-sm text-muted mt-2">
            Total pass revenue: <strong className="text-dark-green">${totalRevenue.toFixed(2)}</strong> from {purchaseCount} purchases
          </p>
        </div>
        <Card className="p-4 border-amber border-2 bg-cream text-right">
          <div className={`font-bold text-lg ${priceColor}`}>{priceWindow}</div>
          <div className="text-xs text-muted font-medium">{timeRemaining}</div>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-green-50 border-green-200">
          <div className="text-sm font-bold text-green-800 uppercase">Registered</div>
          <div className="text-3xl font-bold text-green-900 mt-1">{totalRegistered}</div>
        </Card>
        <Card className="bg-cream border-amber">
          <div className="text-sm font-bold text-amber uppercase">Free</div>
          <div className="text-3xl font-bold text-amber mt-1">{freeCount}</div>
        </Card>
        <Card className="bg-cream border-forest-green">
          <div className="text-sm font-bold text-forest-green uppercase">Evergreen</div>
          <div className="text-3xl font-bold text-forest-green mt-1">{evergreenCount}</div>
        </Card>
        <Card className="bg-forest-green text-white border-none shadow-md">
          <div className="text-sm font-bold text-tan uppercase">All-Access</div>
          <div className="text-3xl font-bold text-white mt-1">{aaCount}</div>
        </Card>
        <Card className="bg-tan border-tan/80">
          <div className="text-sm font-bold text-dark-green uppercase">Comped</div>
          <div className="text-3xl font-bold text-dark-green mt-1">{compedCount}</div>
        </Card>
      </div>

      <Card className="p-0 border border-gray-200 overflow-hidden">
        <button onClick={() => setShowCompPanel(!showCompPanel)} className="w-full bg-gray-50 px-6 py-4 flex justify-between items-center hover:bg-gray-100 transition">
          <div className="font-bold text-dark-green flex items-center gap-2"><SafeIcon name="Gift" className="text-teal-600"/> Grant a Comped Pass</div>
          <SafeIcon name={showCompPanel ? "ChevronUp" : "ChevronDown"} className="text-gray-500"/>
        </button>
        {showCompPanel && (
          <form onSubmit={handleQuickGrant} className="p-6 bg-white border-t border-gray-200 grid md:grid-cols-5 gap-4 items-end">
            <Input label="Email address" type="email" required value={compForm.email} onChange={e => setCompForm({...compForm, email: e.target.value})} className="md:col-span-1" />
            <Input label="Name (Optional)" value={compForm.name} onChange={e => setCompForm({...compForm, name: e.target.value})} className="md:col-span-1" />
            <div className="flex flex-col gap-1 md:col-span-1">
              <label className="text-sm font-medium text-dark-green">Reason</label>
              <select className="px-4 py-2 rounded-md border border-amber bg-cream focus:ring-2 focus:ring-forest-green" value={compForm.reason} onChange={e => setCompForm({...compForm, reason: e.target.value})}>
                <option>Speaker</option>
                <option>Ministry Partner</option>
                <option>Scholarship</option>
                <option>Beta Tester</option>
                <option>Director Discretion</option>
                <option>Other</option>
              </select>
            </div>
            <Input label="Notes" value={compForm.notes} onChange={e => setCompForm({...compForm, notes: e.target.value})} className="md:col-span-1" />
            <Button type="submit" className="md:col-span-1 py-2">Grant All-Access</Button>
          </form>
        )}
      </Card>

      <Card className="flex flex-col md:flex-row gap-4 items-end bg-white border border-gray-200 shadow-sm">
        <div className="flex-1 w-full">
          <Input placeholder="Search name or email..." value={search} onChange={e => setSearch(e.target.value)} icon="Search" />
        </div>
        <div className="flex flex-col gap-1 w-full md:w-auto">
          <label className="text-xs font-bold text-gray-500 uppercase">Pass Type</label>
          <select className="border border-gray-300 rounded px-3 py-2 text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option>All</option><option>Free</option><option>Evergreen</option><option>All-Access</option><option>Comped</option>
          </select>
        </div>
        <div className="flex flex-col gap-1 w-full md:w-auto">
          <label className="text-xs font-bold text-gray-500 uppercase">Source</label>
          <select className="border border-gray-300 rounded px-3 py-2 text-sm" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
             <option>All</option><option>Skillplate</option><option>Manual</option><option>Comped</option><option>Unknown</option>
          </select>
        </div>
        <div className="flex flex-col gap-1 w-full md:w-auto">
          <label className="text-xs font-bold text-gray-500 uppercase">Sort By</label>
          <select className="border border-gray-300 rounded px-3 py-2 text-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
             <option>Registered (Newest)</option><option>Name A-Z</option><option>Pass Type</option><option>Purchase Date</option>
          </select>
        </div>
        <Button variant="outline" className="w-full md:w-auto text-sm py-2" onClick={() => exportCSV(filteredCamperList)}>
          <SafeIcon name="Download" /> Export CSV
        </Button>
      </Card>

      {selectedIds.length > 0 && (
        <div className="bg-forest-green text-white px-6 py-3 rounded-md flex items-center justify-between shadow-md animate-fadeIn">
          <div className="font-bold">{selectedIds.length} campers selected</div>
          <div className="flex gap-3">
            <Button variant="ghost" className="text-white hover:bg-white/20 text-sm" onClick={() => exportCSV(campers.filter(c => selectedIds.includes(c.id)))}>Export Selected</Button>
            <Button variant="secondary" className="text-sm" onClick={sendBulkReminder}>Send Access Reminder</Button>
          </div>
        </div>
      )}

      <Card className="p-0 overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-center w-10">
                  <input type="checkbox" onChange={e => setSelectedIds(e.target.checked ? filteredCamperList.map(c => c.id) : [])} checked={filteredCamperList.length > 0 && selectedIds.length === filteredCamperList.length} />
                </th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Camper</th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Registered</th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Pass Type</th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Price Paid</th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Expires</th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr><td colSpan="9" className="text-center py-12 text-gray-400">Loading campers...</td></tr>
              ) : filteredCamperList.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-16">
                    <SafeIcon name="Users" className="text-4xl text-gray-300 mx-auto mb-3" />
                    {totalRegistered === 0 ? (
                      <p className="text-gray-500">No campers registered yet. Share your camp registration link to get started.</p>
                    ) : (
                      <p className="text-gray-500">No campers match your current filters. <button onClick={() => {setSearch(''); setTypeFilter('All'); setSourceFilter('All');}} className="text-forest-green underline">Clear filters</button></p>
                    )}
                  </td>
                </tr>
              ) : filteredCamperList.map(camper => (
                <tr key={camper.id} className="hover:bg-tan/20 transition-colors">
                  <td className="px-4 py-3 text-center">
                    <input type="checkbox" checked={selectedIds.includes(camper.id)} onChange={e => {
                      if (e.target.checked) setSelectedIds([...selectedIds, camper.id]);
                      else setSelectedIds(selectedIds.filter(id => id !== camper.id));
                    }} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-dark-green text-sm">{camper.name}</div>
                    <div className="text-xs text-muted">{camper.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {camper.created_at ? new Date(camper.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wide ${
                      ['allaccess', 'evergreen-aap'].includes(camper.displayPassType) ? 'bg-forest-green text-white' : 
                      camper.displayPassType === 'comped' ? 'bg-teal-600 text-white' : 
                      camper.displayPassType === 'evergreen' ? 'bg-forest-green/20 text-forest-green' :
                      'bg-amber/20 text-amber'
                    }`}>
                      {camper.displayPassType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 capitalize">{camper.source}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700">
                    {camper.pricePaid > 0 ? `$${parseFloat(camper.pricePaid).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {camper.expiresAt ? new Date(camper.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-sm uppercase ${
                      camper.isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {camper.isExpired ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowActions camper={camper} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-0 border border-tan overflow-hidden bg-cream">
        <button onClick={() => { setShowWebhookPanel(!showWebhookPanel); checkWebhooks(); }} className="w-full px-6 py-4 flex justify-between items-center hover:bg-tan/30 transition">
          <div className="font-bold text-dark-green flex items-center gap-2"><SafeIcon name="Activity" className="text-amber"/> Webhook Reconciliation</div>
          <SafeIcon name={showWebhookPanel ? "ChevronUp" : "ChevronDown"} className="text-gray-500"/>
        </button>
        {showWebhookPanel && (
          <div className="p-6 border-t border-tan bg-white">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                Last checked: {new Date().toLocaleString()} — {webhookMisses.length === 0 ? <span className="text-forest-green font-bold">No issues found</span> : <span className="text-red-600 font-bold">{webhookMisses.length} mismatches found</span>}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" className="text-xs py-1" onClick={checkWebhooks}>Refresh Check</Button>
                <Button variant="ghost" className="text-xs py-1" onClick={() => setActiveModal('webhook_log')}>View Full Log</Button>
              </div>
            </div>
            {webhookMisses.length > 0 && (
              <div className="space-y-3">
                {webhookMisses.map((miss, idx) => (
                  <div key={idx} className="bg-red-50 border border-red-200 p-4 rounded-md flex justify-between items-center">
                    <div>
                      <div className="font-bold text-red-900">{miss.user.name} ({miss.user.email})</div>
                      <div className="text-xs text-red-700 mt-1">Webhook fired {new Date(miss.log.created_at).toLocaleString()}, but user is still {miss.user.pass_type}.</div>
                    </div>
                    <Button onClick={() => fixMissedWebhook(miss)} className="bg-red-600 hover:bg-red-700 text-sm py-1.5"><SafeIcon name="Tool" className="mr-1"/> Fix: Upgrade Now</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {activeModal === 'upgrade' && activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-cream rounded-lg shadow-xl w-full max-w-md p-6 border border-tan">
            <h2 className="text-xl font-bold text-dark-green mb-2">Upgrade {activeUser.name}</h2>
            <p className="text-sm text-muted mb-6">This gives them full camp access immediately.</p>
            <div className="space-y-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-dark-green">Select reason:</label>
                <select className="px-4 py-2 rounded-md border border-amber bg-white focus:ring-2 focus:ring-forest-green" value={upgradeReason} onChange={e => setUpgradeReason(e.target.value)}>
                  <option>Skillplate Webhook Fix</option>
                  <option>Manual Payment Received</option>
                  <option>Other</option>
                </select>
              </div>
              <Textarea label="Notes (Internal)" value={upgradeNotes} onChange={e => setUpgradeNotes(e.target.value)} className="bg-white min-h-[80px]" />
              <div className="flex gap-3 pt-4 border-t border-tan">
                <Button onClick={() => handleExecuteUpgrade(false)} className="flex-1">Confirm Upgrade</Button>
                <Button variant="ghost" onClick={() => setActiveModal(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'comp' && activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-cream rounded-lg shadow-xl w-full max-w-md p-6 border border-tan">
            <h2 className="text-xl font-bold text-dark-green mb-2">Comp {activeUser.name}</h2>
            <p className="text-sm text-muted mb-6">Grants free lifetime All-Access.</p>
            <div className="space-y-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-dark-green">Select reason:</label>
                <select className="px-4 py-2 rounded-md border border-amber bg-white focus:ring-2 focus:ring-forest-green" value={upgradeReason} onChange={e => setUpgradeReason(e.target.value)}>
                  <option>Speaker</option>
                  <option>Ministry Partner</option>
                  <option>Scholarship</option>
                  <option>Beta Tester</option>
                  <option>Director Discretion</option>
                </select>
              </div>
              <Textarea label="Notes (Internal)" value={upgradeNotes} onChange={e => setUpgradeNotes(e.target.value)} className="bg-white min-h-[80px]" />
              <div className="flex gap-3 pt-4 border-t border-tan">
                <Button onClick={() => handleExecuteUpgrade(true)} className="flex-1 bg-teal-600 hover:bg-teal-700">Grant Comped Pass</Button>
                <Button variant="ghost" onClick={() => setActiveModal(null)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'downgrade' && activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 border-t-4 border-red-600">
            <h2 className="text-xl font-bold text-red-700 mb-2">Downgrade {activeUser.name}?</h2>
            <p className="text-sm text-gray-600 mb-6">They will lose access to All-Access features immediately. This cannot be undone without manually re-upgrading.</p>
            <div className="space-y-4">
              <Input label="Type camper's email to confirm:" value={downgradeEmailConfirm} onChange={e => setDowngradeEmailConfirm(e.target.value)} placeholder={activeUser.email} />
              <div className="flex gap-3 pt-4">
                <Button onClick={handleDowngrade} disabled={downgradeEmailConfirm !== activeUser.email} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Confirm Downgrade</Button>
                <Button variant="ghost" onClick={() => {setActiveModal(null); setDowngradeEmailConfirm('');}}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'extend' && activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-dark-green mb-4">Extend Access for {activeUser.name}</h2>
            <Input type="date" label="New Expiration Date" value={extendDate} onChange={e => setExtendDate(e.target.value)} />
            <div className="flex gap-3 pt-6">
              <Button onClick={handleExtend} className="flex-1">Save Extension</Button>
              <Button variant="ghost" onClick={() => setActiveModal(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'profile' && activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-cream rounded-lg shadow-xl w-full max-w-lg p-0 overflow-hidden border border-tan">
            <div className="p-6 bg-white border-b border-gray-200 flex justify-between items-start">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-forest-green rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {activeUser.name?.charAt(0) || 'C'}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-dark-green">{activeUser.name}</h2>
                  <p className="text-muted text-sm">{activeUser.email}</p>
                  <span className={`mt-1 inline-block px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${['allaccess', 'evergreen-aap'].includes(activeUser.displayPassType) ? 'bg-forest-green text-white' : activeUser.displayPassType === 'comped' ? 'bg-teal-600 text-white' : 'bg-amber/20 text-amber'}`}>
                    {activeUser.displayPassType} Pass
                  </span>
                </div>
              </div>
              <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-gray-700"><SafeIcon name="X" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-[10px] text-gray-500 uppercase font-bold">Genre</div>
                  <div className="text-sm font-medium capitalize">{activeUser.genre || '-'}</div>
                </div>
                <div className="bg-white p-3 rounded border border-gray-200">
                  <div className="text-[10px] text-gray-500 uppercase font-bold">Journey Stage</div>
                  <div className="text-sm font-medium capitalize">{activeUser.journey_stage ? activeUser.journey_stage.replace('_', ' ') : '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'history' && activeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-0 overflow-hidden flex flex-col max-h-[80vh]">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><SafeIcon name="Clock" /> Pass History: {activeUser.name}</h2>
              <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-gray-700"><SafeIcon name="X" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              {activeUser.history.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No pass records found.</p>
              ) : (
                activeUser.history.map((h, i) => (
                  <div key={i} className="border border-gray-200 rounded-md p-4 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${['allaccess', 'evergreen-aap'].includes(h.pass_type) ? 'bg-forest-green text-white' : h.pass_type === 'comped' ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-700'}`}>{h.pass_type}</span>
                      <span className="text-xs text-gray-500">{new Date(h.purchased_at).toLocaleString()}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                      <div><span className="text-gray-500">Source:</span> <span className="capitalize font-medium">{h.source}</span></div>
                      <div><span className="text-gray-500">Price Paid:</span> <span className="font-medium">${parseFloat(h.price_paid || 0).toFixed(2)}</span></div>
                      <div><span className="text-gray-500">Expires:</span> <span className="font-medium">{h.expires_at ? new Date(h.expires_at).toLocaleDateString() : '—'}</span></div>
                      <div><span className="text-gray-500">Admin:</span> <span className="font-medium">{h.owner_id}</span></div>
                    </div>
                    {h.notes && <div className="mt-3 pt-2 border-t border-gray-200 text-xs italic text-gray-600">Notes: {h.notes}</div>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {activeModal === 'webhook_log' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-0 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><SafeIcon name="List" /> Full Webhook Log (Last 50)</h2>
              <button onClick={() => setActiveModal(null)} className="text-gray-400 hover:text-gray-700"><SafeIcon name="X" /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-0">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-100 sticky top-0"><tr><th className="p-3">Timestamp</th><th className="p-3">Email</th><th className="p-3">Product</th><th className="p-3">Status</th></tr></thead>
                <tbody className="divide-y divide-gray-200">
                  {webhookLogs.slice(0, 50).map((log, i) => {
                    let email = 'Unknown', product = 'Unknown';
                    try { 
                      const p = JSON.parse(log.payload); 
                      email = p?.user?.email || 'Unknown'; 
                      product = p?.items?.[0]?.product_name || 'Unknown'; 
                    } catch(e) {
                      console.error('Error parsing webhook log payload', e);
                    }
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-3 text-xs text-gray-500 whitespace-nowrap">{new Date(log.created_at || Date.now()).toLocaleString()}</td>
                        <td className="p-3 font-medium text-gray-800">{email}</td>
                        <td className="p-3 text-gray-600 text-xs truncate max-w-xs">{product}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${log.status === 'Success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{log.status}</span>
                          {log.error_message && <div className="text-[10px] text-red-500 mt-1">{log.error_message}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}